/**
 * Feedback primitives for demo workspaces.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback } from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../../design-system/tokens.js";
import {
  ToastViewport as SharedToastViewport,
  useToast as useSharedToast,
  type ToastApi as SharedToastApi,
  type ToastMessage,
  type ToastTone,
  type ToastViewportProps,
} from "../Toast.js";
import { MONO_FONT, OVERRIDE, OVERRIDE_SOFT } from "./styles.js";

export type DemoBadgeTone = "neutral" | "ok" | "warn" | "danger" | "seam" | "override";

export interface DemoBadgeProps {
  readonly children: React.ReactNode;
  readonly tone?: DemoBadgeTone;
  readonly dot?: boolean;
  readonly mono?: boolean;
  readonly title?: string;
}

const BADGE_TONE: Readonly<
  Record<DemoBadgeTone, { readonly bg: string; readonly dot: string; readonly fg: string }>
> = {
  neutral: { bg: "rgba(148, 163, 184, 0.12)", dot: GRAY[400], fg: GRAY[700] },
  ok: { bg: SAFE[100], dot: SAFE[500], fg: SAFE[700] },
  warn: { bg: "#fef3c7", dot: "#f59e0b", fg: "#92400e" },
  danger: { bg: WARN[100], dot: WARN[500], fg: WARN[700] },
  seam: { bg: PRIMARY[100], dot: PRIMARY[500], fg: PRIMARY[700] },
  override: { bg: OVERRIDE_SOFT, dot: OVERRIDE, fg: OVERRIDE },
};

export function DemoBadge({
  children,
  tone = "neutral",
  dot = false,
  mono = false,
  title,
}: DemoBadgeProps): React.JSX.Element {
  const palette = BADGE_TONE[tone];
  return (
    <span
      title={title}
      style={{
        alignItems: "center",
        background: palette.bg,
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 999,
        color: palette.fg,
        display: "inline-flex",
        fontFamily: mono ? MONO_FONT : undefined,
        fontSize: 11,
        gap: 6,
        lineHeight: 1,
        padding: "0.25rem 0.6rem",
        whiteSpace: "nowrap",
      }}
    >
      {dot ? (
        <span
          aria-hidden="true"
          style={{
            background: palette.dot,
            borderRadius: 999,
            display: "inline-block",
            flexShrink: 0,
            height: 8,
            width: 8,
          }}
        />
      ) : null}
      {children}
    </span>
  );
}

export type DemoToastTone = "info" | "ok" | "error";

export interface DemoToast {
  readonly id: number;
  readonly message: string;
  readonly tone?: DemoToastTone;
}

export interface DemoToastApi {
  readonly toast: (message: string, tone?: DemoToastTone) => void;
  readonly toasts: readonly ToastMessage[];
  readonly dismiss: (id: number) => void;
}

function mapToastTone(tone: DemoToastTone): ToastTone {
  if (tone === "ok") return "success";
  return tone;
}

export function useDemoToast(durationMs = 1600): DemoToastApi {
  const api: SharedToastApi = useSharedToast(durationMs);
  const toast = useCallback(
    (message: string, tone: DemoToastTone = "info"): void => {
      api.toast(message, mapToastTone(tone));
    },
    [api],
  );
  return { toast, toasts: api.toasts, dismiss: api.dismiss };
}

export function DemoToastViewport(props: ToastViewportProps): React.JSX.Element | null {
  return <SharedToastViewport {...props} />;
}
